// src/pages/DistrictMaps.js
import React, { useState, useEffect, useRef } from 'react';
import { Map, MapPin, Loader } from 'lucide-react';
import { point as turfPoint, booleanPointInPolygon } from '@turf/turf';
import { WELD_SCHOOL_DISTRICTS_FILE, getWeldSchoolDistrictFriendlyName } from '../utils/weldSchoolDistricts';
import './DistrictMaps.css';

const LARIMER_GIS = 'https://maps1.larimer.org/arcgis/rest/services/MapServices/voterMap/MapServer';
const TIGERWEB = 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb';

const LAYERS = [
  { id: 'congressional', label: 'Congressional', color: '#ae3030', source: 'tigerweb', service: 'Legislative', layerId: 0 },
  { id: 'stateSenate', label: 'State Senate', color: '#182B45', source: 'tigerweb', service: 'Legislative', layerId: 1 },
  { id: 'stateHouse', label: 'State House', color: '#2e7d32', source: 'tigerweb', service: 'Legislative', layerId: 2 },
  { id: 'county', label: 'County Commissioner', color: '#e65100', source: 'larimer', layerId: 6 },
  { id: 'precinct', label: 'Voter Precinct', color: '#b8860b', source: 'larimer', layerId: 2 },
  { id: 'school', label: 'School Board', color: '#6a1b9a', source: 'tigerweb', service: 'School', layerId: 0 },
];

const IframeDistrictMaps = ({ county }) => (
  <div>
    <div className="sec-head">
      <div className="sec-icon"><Map size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
      <div>
        <div className="sec-title">District maps</div>
        <div className="sec-desc">Find your {county.name} districts using the county's official map tool</div>
      </div>
    </div>
    <iframe
      src={county.districtMapUrl}
      width="100%"
      height="600"
      frameBorder="0"
      title={`${county.name} district map`}
      style={{ borderRadius: 6, border: '0.5px solid #ddd', marginBottom: 10 }}
      allowFullScreen
    />
    <div style={{ fontSize: 12, color: '#999', lineHeight: 1.6 }}>
      Source: {county.name} GIS
    </div>
  </div>
);

// --- Weld County: TIGERweb for Congressional/Senate/House (full district
// boundaries, same as Larimer), Turf.js point-in-polygon against local
// GeoJSON for Commissioner and School District. ---

const WELD_GEO_BASE = '/geojson/weld';
const WELD_COMMISSIONER_DISTRICTS_FILE = `${WELD_GEO_BASE}/Commissioner_Districts_open_data_2435841555182118571.geojson`;

const WELD_LAYERS = [
  { id: 'congressional', label: 'Congressional District', color: '#182B45', source: 'tigerweb', service: 'Legislative', layerId: 0 },
  { id: 'stateSenate', label: 'State Senate District', color: '#AE3030', source: 'tigerweb', service: 'Legislative', layerId: 1 },
  { id: 'stateHouse', label: 'State House District', color: '#166534', source: 'tigerweb', service: 'Legislative', layerId: 2 },
  { id: 'county', label: 'County Commissioner District', color: '#6B21A8', source: 'geojson', file: WELD_COMMISSIONER_DISTRICTS_FILE },
  { id: 'school', label: 'School District', color: '#D97706', source: 'geojson', file: WELD_SCHOOL_DISTRICTS_FILE },
];

const geocodeWeldAddress = (addr) => {
  return new Promise((resolve, reject) => {
    if (!window.google) {
      reject(new Error('Google Maps not loaded'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addr }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        reject(new Error('Address not found: ' + status));
      }
    });
  });
};

const WeldDistrictMaps = ({ initialAddress, county }) => {
  const bounds = county?.autocompleteBounds;
  const [address, setAddress] = useState(initialAddress || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeLayers, setActiveLayers] = useState(['congressional']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [districtInfo, setDistrictInfo] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const mapInitialized = useRef(false);
  const overlaysRef = useRef([]);
  const markerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const lastLatLng = useRef(null);
  const geojsonCacheRef = useRef({});
  const autocompleteService = useRef(null);
  const hideTimeout = useRef(null);

  const toggleLayer = (layerId) => {
    setActiveLayers(prev =>
      prev.includes(layerId)
        ? prev.filter(l => l !== layerId)
        : [...prev, layerId]
    );
  };

  const clearOverlays = () => {
    overlaysRef.current.forEach(overlay => {
      if (window.google) window.google.maps.event.clearInstanceListeners(overlay);
      overlay.setMap(null);
    });
    overlaysRef.current = [];
  };

  // Teardown on unmount: clear overlays/marker ourselves so React never has
  // to reconcile DOM nodes Google Maps already rewrote inside the map div.
  useEffect(() => {
    return () => {
      clearOverlays();
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      googleMapRef.current = null;
      mapInitialized.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch + cache a raw GeoJSON file for Turf point-in-polygon lookups
  const loadCachedGeojson = async (file) => {
    if (geojsonCacheRef.current[file]) return geojsonCacheRef.current[file];
    const res = await fetch(file);
    if (!res.ok) throw new Error(`Failed to load ${file}: ${res.status}`);
    const data = await res.json();
    geojsonCacheRef.current[file] = data;
    return data;
  };

  const fetchWeldTigerDistrict = (lat, lng, service, layerId) => {
    const url = `${TIGERWEB}/${service}/MapServer/${layerId}/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&returnGeometry=true&f=geojson`;
    return fetch(url).then(r => r.json());
  };

  const drawWeldOverlay = (map, geojson, color, clickLabel) => {
    if (!geojson || !geojson.features || !geojson.features.length) return;
    const overlay = new window.google.maps.Data();
    overlay.addGeoJson(geojson);
    overlay.setStyle({
      fillColor: color,
      fillOpacity: 0.25,
      strokeColor: color,
      strokeWeight: 2,
      strokeOpacity: 1,
    });
    overlay.setMap(map);
    overlaysRef.current.push(overlay);

    if (clickLabel) {
      overlay.addListener('click', (e) => {
        if (!infoWindowRef.current) {
          infoWindowRef.current = new window.google.maps.InfoWindow();
        }
        const content = document.createElement('div');
        content.style.fontSize = '13px';
        content.style.fontWeight = '500';
        content.textContent = clickLabel;
        infoWindowRef.current.setContent(content);
        infoWindowRef.current.setPosition(e.latLng);
        infoWindowRef.current.open(map);
      });
    }
  };

  const fetchAndDrawWeldLayers = (lat, lng, layers, map) => {
    const pt = turfPoint([lng, lat]);
    const info = {};

    const promises = WELD_LAYERS
      .filter(layer => layers.includes(layer.id))
      .map(layer => {
        if (layer.source === 'tigerweb') {
          // TIGERweb returns the district's full boundary, not just the
          // portion within Weld County — same source Larimer uses.
          return fetchWeldTigerDistrict(lat, lng, layer.service, layer.layerId)
            .then(geojson => {
              drawWeldOverlay(map, geojson, layer.color);
              if (geojson && geojson.features && geojson.features[0] && geojson.features[0].properties) {
                const props = geojson.features[0].properties;
                info[layer.id] = props.NAMELSAD || props.NAME || props.GEOID || 'Found';
              }
            })
            .catch(err => console.log('TIGERweb error for', layer.label, err));
        }
        return loadCachedGeojson(layer.file)
          .then(geojson => {
            const feature = geojson.features.find(f => booleanPointInPolygon(pt, f));
            if (feature) {
              const rawLabel = feature.properties.DISTRICT || feature.properties.NAME || 'Found';
              // The School District GeoJSON code (e.g. "RE-11") gets the same
              // friendly-name treatment used on the voter info tab.
              const label = layer.id === 'school' ? getWeldSchoolDistrictFriendlyName(rawLabel) : rawLabel;
              drawWeldOverlay(map, { type: 'FeatureCollection', features: [feature] }, layer.color, layer.id === 'school' ? label : null);
              info[layer.id] = label;
            }
          })
          .catch(err => console.log('Weld GeoJSON lookup error for', layer.label, err));
      });

    return Promise.all(promises).then(() => info);
  };

  const handleAddressInput = (val) => {
    setAddress(val);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: val, componentRestrictions: { country: 'us' }, bounds },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(p => ({
              main: p.structured_formatting.main_text,
              sub: p.structured_formatting.secondary_text,
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    }
  };

  const runSearch = (searchAddress, layers) => {
    if (!searchAddress.trim()) return;
    if (!window.google) {
      setError('Google Maps is still loading. Please wait a moment and try again.');
      return;
    }
    setLoading(true);
    setError(null);
    setDistrictInfo(null);
    clearOverlays();

    geocodeWeldAddress(searchAddress)
      .then(({ lat, lng }) => {
        lastLatLng.current = { lat, lng };

        if (!mapInitialized.current) {
          googleMapRef.current = new window.google.maps.Map(mapRef.current, {
            zoom: 11,
            center: { lat, lng },
            mapTypeId: 'roadmap',
          });
          mapInitialized.current = true;
          setMapReady(true);
        } else {
          googleMapRef.current.setCenter({ lat, lng });
          googleMapRef.current.setZoom(11);
        }

        if (markerRef.current) markerRef.current.setMap(null);
        markerRef.current = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMapRef.current,
          title: searchAddress,
        });

        return fetchAndDrawWeldLayers(lat, lng, layers, googleMapRef.current);
      })
      .then(info => {
        setDistrictInfo(info);
      })
      .catch(err => {
        console.log('Weld district search error:', err);
        setError('Could not find that address. Please try a full street address including city and state.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const selectAddress = (addr) => {
    const full = `${addr.main}, ${addr.sub}`;
    setAddress(full);
    setShowSuggestions(false);
    runSearch(full, activeLayers);
  };

  const handleSearch = () => {
    runSearch(address, activeLayers);
  };

  useEffect(() => {
    if (lastLatLng.current && googleMapRef.current && districtInfo !== null) {
      clearOverlays();
      const { lat, lng } = lastLatLng.current;
      fetchAndDrawWeldLayers(lat, lng, activeLayers, googleMapRef.current)
        .then(info => setDistrictInfo(info));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayers]);

  useEffect(() => {
    if (initialAddress && window.google) {
      runSearch(initialAddress, activeLayers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddress]);

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><Map size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">District maps</div>
          <div className="sec-desc">Enter your address to see your districts highlighted on the map</div>
        </div>
      </div>

      <div style={{position:'relative', marginBottom:14}}>
        <div style={{display:'flex', gap:8}}>
          <input
            type="text"
            placeholder="123 Main St, Greeley, CO 80631"
            value={address}
            onChange={e => handleAddressInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            onBlur={() => { hideTimeout.current = setTimeout(() => setShowSuggestions(false), 200); }}
            onFocus={() => address.length > 1 && setShowSuggestions(suggestions.length > 0)}
            autoComplete="off"
            style={{flex:1}}
          />
          <button className="btn-red" onClick={handleSearch} disabled={loading}>
            {loading ? <Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> : 'Show map'}
          </button>
        </div>
        {showSuggestions && suggestions.length > 0 && (
          <div style={{
            position:'absolute', top:40, left:0, right:76,
            background:'white', border:'0.5px solid #ddd',
            borderRadius:6, overflow:'hidden', zIndex:100,
            boxShadow:'0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {suggestions.map((s, i) => (
              <div
                key={i}
                onMouseDown={() => selectAddress(s)}
                style={{
                  display:'flex', alignItems:'center', gap:10,
                  padding:'10px 14px', cursor:'pointer',
                  borderBottom:'0.5px solid #eee', fontSize:13,
                }}
                onMouseOver={e => e.currentTarget.style.background='#f5f5f5'}
                onMouseOut={e => e.currentTarget.style.background='white'}
              >
                <span><MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></span>
                <div>
                  <div style={{fontWeight:500}}>{s.main}</div>
                  <div style={{fontSize:11, color:'#888'}}>{s.sub}</div>
                </div>
              </div>
            ))}
            <div style={{padding:'6px 14px', fontSize:10, color:'#aaa', background:'#f9f9f9'}}>
              <Map size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Powered by Google Maps
            </div>
          </div>
        )}
      </div>

      <div className="pills" style={{marginBottom:10}}>
        {WELD_LAYERS.map(layer => (
          <span
            key={layer.id}
            className={`pill ${activeLayers.includes(layer.id) ? 'on' : ''}`}
            style={activeLayers.includes(layer.id)
              ? {background:layer.color, borderColor:layer.color, color:'#fff'}
              : {}
            }
            onClick={() => toggleLayer(layer.id)}
          >
            {layer.id === 'school' && districtInfo?.school ? districtInfo.school : layer.label}
          </span>
        ))}
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div
        style={{
          position: 'relative',
          height: 300,
          borderRadius: 6,
          border: '0.5px solid #ddd',
          marginBottom: 14,
          background: '#f0f4f8',
          overflow: 'hidden',
        }}
      >
        {/* Google Maps owns this node's DOM once created — React never renders children into it. */}
        <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />

        {!mapReady && !loading && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', color: '#999', pointerEvents: 'none',
          }}>
            <div>
              <div style={{fontSize:34}}><Map size={34} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
              <p style={{fontSize:13}}>Enter your address above to see your districts.</p>
            </div>
          </div>
        )}
      </div>

      {districtInfo && Object.keys(districtInfo).length > 0 && (
        <div className="result-grid">
          {WELD_LAYERS
            .filter(l => activeLayers.includes(l.id) && districtInfo[l.id])
            .map(layer => (
              <div key={layer.id} className="result-card">
                <div className="result-label" style={{display:'flex', alignItems:'center', gap:4}}>
                  <span style={{display:'inline-block', width:8, height:8, borderRadius:'50%', background:layer.color, flexShrink:0}}></span>
                  {layer.label}
                </div>
                <div className="result-value">{districtInfo[layer.id]}</div>
              </div>
            ))
          }
        </div>
      )}

      <div style={{marginTop:14, fontSize:12, color:'#999', lineHeight:1.6}}>
        Source: Congressional, State Senate, and State House full district boundaries from U.S. Census Bureau TIGERweb. County Commissioner and School District boundaries from Weld County GIS Open Data.
      </div>
    </div>
  );
};

const DistrictMaps = ({ initialAddress, county }) => {
  if (county?.id === 'weld') {
    return <WeldDistrictMaps initialAddress={initialAddress} county={county} />;
  }

  if (county?.districtMapType === 'iframe') {
    return <IframeDistrictMaps county={county} />;
  }

  return <LarimerDistrictMaps initialAddress={initialAddress} county={county} />;
};

const LarimerDistrictMaps = ({ initialAddress, county }) => {
  const bounds = county?.autocompleteBounds || {
    north: 41.003444, south: 40.259874, east: -104.902038, west: -105.715732,
  };
  const [address, setAddress] = useState(initialAddress || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeLayers, setActiveLayers] = useState(['congressional']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [districtInfo, setDistrictInfo] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const mapInitialized = useRef(false);
  const overlaysRef = useRef([]);
  const markerRef = useRef(null);
  const lastLatLng = useRef(null);
  const autocompleteService = useRef(null);
  const hideTimeout = useRef(null);

  const toggleLayer = (layerId) => {
    setActiveLayers(prev =>
      prev.includes(layerId)
        ? prev.filter(l => l !== layerId)
        : [...prev, layerId]
    );
  };

  const clearOverlays = () => {
    overlaysRef.current.forEach(overlay => {
      if (window.google) window.google.maps.event.clearInstanceListeners(overlay);
      overlay.setMap(null);
    });
    overlaysRef.current = [];
  };

  // Teardown on unmount: clear overlays/marker ourselves so React never has
  // to reconcile DOM nodes Google Maps already rewrote inside the map div.
  useEffect(() => {
    return () => {
      clearOverlays();
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      googleMapRef.current = null;
      mapInitialized.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const geocodeAddress = (addr) => {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        reject(new Error('Google Maps not loaded'));
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          reject(new Error('Address not found: ' + status));
        }
      });
    });
  };

  const handleAddressInput = (val) => {
    setAddress(val);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        {
          input: val,
          componentRestrictions: { country: 'us' },
          bounds,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(p => ({
              main: p.structured_formatting.main_text,
              sub: p.structured_formatting.secondary_text,
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    }
  };

  const selectAddress = (addr) => {
    const full = `${addr.main}, ${addr.sub}`;
    setAddress(full);
    setShowSuggestions(false);
    runSearch(full, activeLayers);
  };

  const fetchLarimerDistrict = (lat, lng, layerId) => {
    const url = `${LARIMER_GIS}/identify?geometry=${lng},${lat}&geometryType=esriGeometryPoint&sr=4326&layers=all:${layerId}&tolerance=2&mapExtent=${lng - 0.1},${lat - 0.1},${lng + 0.1},${lat + 0.1}&imageDisplay=800,600,96&returnGeometry=true&f=json`;
    return fetch(url).then(r => r.json());
  };

  const fetchTigerDistrict = (lat, lng, service, layerId) => {
    const url = `${TIGERWEB}/${service}/MapServer/${layerId}/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&returnGeometry=true&f=geojson`;
    return fetch(url).then(r => r.json());
  };

  const drawGoogleData = (map, geojson, color) => {
    if (!geojson || !geojson.features || !geojson.features.length) return;
    const overlay = new window.google.maps.Data();
    overlay.addGeoJson(geojson);
    overlay.setStyle({
      fillColor: color,
      fillOpacity: 0.15,
      strokeColor: color,
      strokeWeight: 2,
      strokeOpacity: 0.8,
    });
    overlay.setMap(map);
    overlaysRef.current.push(overlay);
  };

  const drawLarimerGeometry = (map, geometry, color) => {
    if (!geometry || !geometry.rings) return;
    const geojson = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: geometry.rings.length > 1 ? 'MultiPolygon' : 'Polygon',
          coordinates: geometry.rings.length > 1
            ? geometry.rings.map(ring => [ring])
            : [geometry.rings[0]],
        },
        properties: {},
      }],
    };
    drawGoogleData(map, geojson, color);
  };

  const fetchAndDrawLayers = (lat, lng, layers, map) => {
    const info = {};
    const promises = LAYERS
      .filter(layer => layers.includes(layer.id))
      .map(layer => {
        if (layer.source === 'larimer') {
          return fetchLarimerDistrict(lat, lng, layer.layerId)
            .then(result => {
              if (result && result.results && result.results.length > 0) {
                const feature = result.results[0];
                const attrs = feature.attributes || {};
                const label =
                  attrs.DISTRICT ||
                  attrs.District ||
                  attrs.LABEL ||
                  attrs.Label ||
                  attrs.NAME ||
                  attrs.Name ||
                  attrs.PRECINCT ||
                  attrs.Precinct ||
                  attrs.COMMID ||
                  String(feature.value || '') ||
                  'Found';
                info[layer.id] = label;
                if (feature.geometry) {
                  drawLarimerGeometry(map, feature.geometry, layer.color);
                }
              }
            })
            .catch(err => console.log('Larimer GIS error for', layer.label, err));
        } else {
          return fetchTigerDistrict(lat, lng, layer.service, layer.layerId)
            .then(geojson => {
              drawGoogleData(map, geojson, layer.color);
              if (geojson && geojson.features && geojson.features[0] && geojson.features[0].properties) {
                const props = geojson.features[0].properties;
                info[layer.id] = props.NAMELSAD || props.NAME || props.GEOID || 'Found';
              }
            })
            .catch(err => console.log('TIGERweb error for', layer.label, err));
        }
      });

    return Promise.all(promises).then(() => info);
  };

  const runSearch = (searchAddress, layers) => {
    if (!searchAddress.trim()) return;
    if (!window.google) {
      setError('Google Maps is still loading. Please wait a moment and try again.');
      return;
    }
    setLoading(true);
    setError(null);
    setDistrictInfo(null);
    clearOverlays();

    geocodeAddress(searchAddress)
      .then(({ lat, lng }) => {
        lastLatLng.current = { lat, lng };

        if (!mapInitialized.current) {
          googleMapRef.current = new window.google.maps.Map(mapRef.current, {
            zoom: 11,
            center: { lat, lng },
            mapTypeId: 'roadmap',
          });
          mapInitialized.current = true;
          setMapReady(true);
        } else {
          googleMapRef.current.setCenter({ lat, lng });
          googleMapRef.current.setZoom(11);
        }

        if (markerRef.current) markerRef.current.setMap(null);
        markerRef.current = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMapRef.current,
          title: searchAddress,
        });

        return fetchAndDrawLayers(lat, lng, layers, googleMapRef.current);
      })
      .then(info => {
        setDistrictInfo(info);
      })
      .catch(err => {
        console.log('Search error:', err);
        setError('Could not find that address. Please try a full street address including city and state.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearch = () => {
    runSearch(address, activeLayers);
  };

  useEffect(() => {
    if (lastLatLng.current && googleMapRef.current && districtInfo !== null) {
      clearOverlays();
      const { lat, lng } = lastLatLng.current;
      fetchAndDrawLayers(lat, lng, activeLayers, googleMapRef.current)
        .then(info => setDistrictInfo(info));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayers]);

  useEffect(() => {
    if (initialAddress && window.google) {
      runSearch(initialAddress, activeLayers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddress]);

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><Map size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">District maps</div>
          <div className="sec-desc">Enter your address to see your districts highlighted on the map</div>
        </div>
      </div>

      <div style={{position:'relative', marginBottom:14}}>
        <div style={{display:'flex', gap:8}}>
          <input
            type="text"
            placeholder="123 Main St, Fort Collins, CO 80521"
            value={address}
            onChange={e => handleAddressInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            onBlur={() => { hideTimeout.current = setTimeout(() => setShowSuggestions(false), 200); }}
            onFocus={() => address.length > 1 && setShowSuggestions(suggestions.length > 0)}
            autoComplete="off"
            style={{flex:1}}
          />
          <button className="btn-red" onClick={handleSearch} disabled={loading}>
            {loading ? <Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> : 'Show map'}
          </button>
        </div>
        {showSuggestions && suggestions.length > 0 && (
          <div style={{
            position:'absolute', top:40, left:0, right:76,
            background:'white', border:'0.5px solid #ddd',
            borderRadius:6, overflow:'hidden', zIndex:100,
            boxShadow:'0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {suggestions.map((s, i) => (
              <div
                key={i}
                onMouseDown={() => selectAddress(s)}
                style={{
                  display:'flex', alignItems:'center', gap:10,
                  padding:'10px 14px', cursor:'pointer',
                  borderBottom:'0.5px solid #eee', fontSize:13,
                }}
                onMouseOver={e => e.currentTarget.style.background='#f5f5f5'}
                onMouseOut={e => e.currentTarget.style.background='white'}
              >
                <span><MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></span>
                <div>
                  <div style={{fontWeight:500}}>{s.main}</div>
                  <div style={{fontSize:11, color:'#888'}}>{s.sub}</div>
                </div>
              </div>
            ))}
            <div style={{padding:'6px 14px', fontSize:10, color:'#aaa', background:'#f9f9f9'}}>
              <Map size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Powered by Google Maps
            </div>
          </div>
        )}
      </div>

      <div className="pills" style={{marginBottom:10}}>
        {LAYERS.map(layer => (
          <span
            key={layer.id}
            className={`pill ${activeLayers.includes(layer.id) ? 'on' : ''}`}
            style={activeLayers.includes(layer.id)
              ? {background:layer.color, borderColor:layer.color, color:'#fff'}
              : {}
            }
            onClick={() => toggleLayer(layer.id)}
          >
            {layer.label}
          </span>
        ))}
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div
        style={{
          position: 'relative',
          height: 300,
          borderRadius: 6,
          border: '0.5px solid #ddd',
          marginBottom: 14,
          background: '#f0f4f8',
          overflow: 'hidden',
        }}
      >
        {/* Google Maps owns this node's DOM once created — React never renders children into it. */}
        <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />

        {!mapReady && !loading && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', color: '#999', pointerEvents: 'none',
          }}>
            <div>
              <div style={{fontSize:34}}><Map size={34} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
              <p style={{fontSize:13}}>Enter an address above to load your district map</p>
            </div>
          </div>
        )}
      </div>

      {districtInfo && Object.keys(districtInfo).length > 0 && (
        <div className="result-grid">
          {LAYERS
            .filter(l => activeLayers.includes(l.id) && districtInfo[l.id])
            .map(layer => (
              <div key={layer.id} className="result-card">
                <div className="result-label" style={{display:'flex', alignItems:'center', gap:4}}>
                  <span style={{display:'inline-block', width:8, height:8, borderRadius:'50%', background:layer.color, flexShrink:0}}></span>
                  {layer.label}
                </div>
                <div className="result-value">{districtInfo[layer.id]}</div>
              </div>
            ))
          }
        </div>
      )}

      <div style={{marginTop:14, fontSize:12, color:'#999', lineHeight:1.6}}>
        Source: Congressional, State Senate, State House, and School Board boundaries from U.S. Census Bureau TIGERweb. County Commissioner and Voter Precinct data from Larimer County GIS.
      </div>
    </div>
  );
};

export default DistrictMaps;
